import { createReducer, on } from '@ngrx/store';

import { AccessTypeSelection } from '../../../common/enums/access-type-selection.enum';
import {
    bulkGrantPermissionSuccess,
    bulkRemovePermissionSuccess,
    grantPermissionSuccess,
    removePermissionSuccess,
} from '../actions/access-permissions.actions';
import {
    accountUsersDataLoadedError,
    accountUsersDataLoadedSuccess,
    clientSegmentContentLoadedError,
    clientSegmentContentLoadedRedirect,
    clientSegmentContentLoadedSuccess,
} from '../actions/account-permissions-api.action';
import {
    loadAccountUsers,
    loadClientSegmentContent,
    loadDefaultClientSegmentContent,
} from '../actions/account-permissions-page.action';
import {
    ineligibleScenariosErrorForGrantor,
    ineligibleScenariosSuccessForGrantee,
    ineligibleScenariosSuccessForGrantor,
} from '../actions/ineligible-scenarios.action';
import {
    updateSelectedViewTypeForMyAccounts,
    updateSelectedViewTypeForOtherAccounts,
    updateSelectedViewTypeForUsers,
} from '../actions/view-type.action';
import { AccountPermissionsInitialState } from '../state/account-permissions-initial.state';

const API_SUCCESS = {
    isLoading: false,
    error: null,
};

const API_ERROR = {
    data: null,
    isLoading: false,
};

const getUsersWithRemainingAccess = (newAccounts, users = []) => {
    const remainingGranteeIds = new Set(
        newAccounts.flatMap((account) => account.grantees.map((grantee) => String(grantee.clientPoid))),
    );

    return users.filter((user) => remainingGranteeIds.has(String(user.clientPoid)));
};

export const accountPermissionReducer = createReducer(
    AccountPermissionsInitialState,
    on(loadAccountUsers, (state) => ({
        ...state,
        accountUsers: {
            ...state.accountUsers,
            isLoading: true,
            hasLoaded: false,
        },
    })),

    on(accountUsersDataLoadedSuccess, (state, { data }) => ({
        ...state,
        accountUsers: {
            ...API_SUCCESS,
            data: data.accountsUsers,
            hasLoaded: true,
        },
        hasIneligibleError: data.hasIneligibleError,
    })),

    on(accountUsersDataLoadedError, (state, { error }) => ({
        ...state,
        accountUsers: {
            ...API_ERROR,
            error,
            hasLoaded: true,
        },
    })),

    on(loadClientSegmentContent, (state) => ({
        ...state,
        clientSegment: {
            ...state.clientSegment,
            isLoading: true,
            hasLoaded: false,
        },
    })),

    on(loadDefaultClientSegmentContent, (state, { data }) => ({
        ...state,
        clientSegment: {
            data,
            hasLoaded: true,
            isLoading: false,
            error: null,
        },
    })),

    on(clientSegmentContentLoadedSuccess, (state, { data }) => ({
        ...state,
        clientSegment: {
            ...API_SUCCESS,
            data,
            hasLoaded: true,
        },
    })),

    on(clientSegmentContentLoadedError, (state, { error }) => ({
        ...state,
        clientSegment: {
            ...API_ERROR,
            error,
            hasLoaded: true,
        },
    })),

    on(clientSegmentContentLoadedRedirect, (state, { error }) => ({
        ...state,
        clientSegment: {
            ...API_ERROR,
            isLoading: true,
            error,
            hasLoaded: true,
        },
    })),

    on(updateSelectedViewTypeForUsers, (state, { viewType }) => ({
        ...state,
        selectedViewTypeForUsers: viewType,
    })),

    on(updateSelectedViewTypeForMyAccounts, (state, { viewType }) => ({
        ...state,
        selectedViewTypeForMyAccounts: viewType,
    })),

    on(updateSelectedViewTypeForOtherAccounts, (state, { viewType }) => ({
        ...state,
        selectedViewTypeForOtherAccounts: viewType,
    })),

    on(grantPermissionSuccess, (state, { data, roleType, accessLevel }) => {
        const { accountId, serviceAgreementId, granteeId } = data;
        let newAccounts;
        if (accessLevel === AccessTypeSelection.INFORMATION_ONLY) {
            newAccounts = [
                ...state.accountUsers.data.ownAccounts.map((account) => {
                    if (
                        account.accountId === accountId &&
                        account.serviceAgreementId === serviceAgreementId
                    ) {
                        return {
                            ...account,
                            grantees: [
                                ...account.grantees,
                                {
                                    clientPoid: String(granteeId),
                                    roleType: roleType,
                                },
                            ],
                        };
                    }
                    return account;
                }),
            ];
            return {
                ...state,
                accountUsers: {
                    ...state.accountUsers,
                    data: {
                        ...state.accountUsers.data,
                        ownAccounts: newAccounts,
                    },
                },
            };
        } else {
            return { ...state };
        }
    }),

    on(bulkGrantPermissionSuccess, (state, { data, roleType, accessLevel }) => {
        if (accessLevel !== AccessTypeSelection.INFORMATION_ONLY) {
            return state;
        }

        const successfulGrants = data.results?.filter((result) => !!result.confirmationNumber) ?? [];

        if (!successfulGrants.length) {
            return state;
        }

        const newAccounts = state.accountUsers.data.ownAccounts.map((account) => {
            const matchingGrant = successfulGrants.find(
                (result) =>
                    result.accountId === account.accountId &&
                    result.serviceAgreementId === account.serviceAgreementId,
            );

            if (!matchingGrant) {
                return account;
            }

            const granteeId = String(matchingGrant.granteeId);
            const existingGranteeIndex = account.grantees.findIndex(
                (grantee) => grantee.clientPoid === granteeId,
            );

            if (existingGranteeIndex >= 0) {
                const updatedGrantees = [...account.grantees];
                updatedGrantees[existingGranteeIndex] = {
                    ...updatedGrantees[existingGranteeIndex],
                    roleType: roleType,
                };
                return {
                    ...account,
                    grantees: updatedGrantees,
                };
            }

            return {
                ...account,
                grantees: [
                    ...account.grantees,
                    {
                        clientPoid: granteeId,
                        roleType: roleType,
                    },
                ],
            };
        });

        return {
            ...state,
            accountUsers: {
                ...state.accountUsers,
                data: {
                    ...state.accountUsers.data,
                    ownAccounts: newAccounts,
                },
            },
        };
    }),

    on(ineligibleScenariosSuccessForGrantor, (state, { data }) => {
        return {
            ...state,
            grantorIsIneligibleFor: data,
        };
    }),

    on(ineligibleScenariosSuccessForGrantee, (state, { granteeIneligibleReason, userDetails }) => {
        return {
            ...state,
            accountUsers: {
                ...state.accountUsers,
                data: {
                    ...state.accountUsers.data,
                    users: [
                        ...state.accountUsers.data.users.map((user) => {
                            if (user.clientPoid === userDetails.clientPoid) {
                                return {
                                    ...user,
                                    granteeIneligibleReason,
                                };
                            }
                            return user;
                        }),
                    ],
                },
            },
        };
    }),

    on(ineligibleScenariosErrorForGrantor, (state) => {
        return {
            ...state,
            hasIneligibleError: true,
        };
    }),

    // this reducer removes a grantee with 'granteeId'
    // from an account with 'accountId' after the successful response from API
    on(removePermissionSuccess, (state, { data }) => {
        const { accountId, serviceAgreementId, granteeId } = data;

        const newAccounts = [
            ...state.accountUsers.data.ownAccounts.map((account) => {
                if (
                    account.accountId === accountId &&
                    account.serviceAgreementId === serviceAgreementId
                ) {
                    return {
                        ...account,
                        grantees: [
                            ...account.grantees.filter(
                                (grantee) => grantee.clientPoid !== granteeId,
                            ),
                        ],
                    };
                }
                return account;
            }),
        ];
        return {
            ...state,
            accountUsers: {
                ...state.accountUsers,
                data: {
                    ...state.accountUsers.data,
                    ownAccounts: newAccounts,
                },
            },
        };
    }),

    on(bulkRemovePermissionSuccess, (state, { data }) => {
        const successfulRemovals = data.results?.filter((result) => !!result.confirmationNumber) ?? [];

        if (!successfulRemovals.length) {
            return state;
        }

        const newAccounts = state.accountUsers.data.ownAccounts.map((account) => {
            const matchingRemoval = successfulRemovals.find(
                (result) =>
                    result.accountId === account.accountId &&
                    result.serviceAgreementId === account.serviceAgreementId,
            );

            if (!matchingRemoval) {
                return account;
            }

            return {
                ...account,
                grantees: account.grantees.filter(
                    (grantee) => grantee.clientPoid !== matchingRemoval.granteeId,
                ),
            };
        });

        const usersWithAccess = getUsersWithRemainingAccess(
            newAccounts,
            state.accountUsers.data.users,
        );

        return {
            ...state,
            accountUsers: {
                ...state.accountUsers,
                data: {
                    ...state.accountUsers.data,
                    ownAccounts: newAccounts,
                    users: usersWithAccess,
                },
            },
        };
    }),
);
